import { registerLocaleData, Location } from '@angular/common';
import { provideMarkdown } from 'ngx-markdown';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeAU from '@angular/common/locales/en-AU';
import { ApplicationConfig, importProvidersFrom, LOCALE_ID, isDevMode } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withPreloading, PreloadAllModules, NoPreloading, withComponentInputBinding, withRouterConfig, TitleStrategy } from '@angular/router';
import { LocaleSettings } from '@jms/model';
import { AuthorizeGuard } from '@jms/tenant/module.jms.authorisation';
import { AuthTokenService } from '@jms/utils/util.authentication';
import { AuthenticationInterceptor } from '@jms/utils/util.authentication/src/authentication.interceptor';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { GOOGLE_MAPS_API_CONFIG } from '@ng-maps/google';
import { provideQueryDevTools } from '@ngneat/query-devtools';
import { CustomPageTitleStrategy } from '@workbuddy/ui/services';
import { CODE_PICKER_SERVICE } from '@jms/ui/ui.code.picker.pro';
import { CodePickerService } from '@jms/ui/ui.standalone.services/code.picker.service';
import { FAVICON_CONFIG } from '@jms/ui/ng-favicon';
import { MessageService } from 'primeng/api';
import { provideLoadingBarInterceptor } from '@ngx-loading-bar/http-client';
import { AuthGuard } from './guards/auth.guard';
import { HelpService } from './components/help.nav/services';
import { app_routes } from './application.routes';
import { ApplicationModule } from './application.module';
import { provideServiceWorker } from '@angular/service-worker';

registerLocaleData(localeAU, 'en-AU');
LocaleSettings.setLocale(ConfigService?.environment?.locale ?? 'en-AU');

const isPreviewOrPublic = window.location.href.indexOf('forms') > -1 || window.location.href.indexOf('preview') > -1 || window.location.href.indexOf('public') > -1;

export const appConfig: ApplicationConfig = {
    providers: [
        // Router
        provideRouter(
            app_routes,
            withPreloading(isPreviewOrPublic ? NoPreloading : PreloadAllModules),
            withComponentInputBinding(),
            withRouterConfig({ paramsInheritanceStrategy: 'always' })
        ),

        // HTTP Client with DI-based interceptors
        provideHttpClient(withXhr(), withInterceptorsFromDi()),

        // Interceptors
        {
            provide: HTTP_INTERCEPTORS,
            useFactory: (location: Location, authTokenService: AuthTokenService) => new AuthenticationInterceptor(location, authTokenService),
            multi: true,
            deps: [Location, AuthTokenService]
        },
        provideLoadingBarInterceptor(),

        // Locale
        { provide: LOCALE_ID, useValue: 'en-AU' },

        // CDK Overlay
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },

        // Guards
        AuthGuard,
        AuthorizeGuard,

        // Services
        MessageService,
        HelpService,

        // Favicon
        { provide: FAVICON_CONFIG, useValue: { color: '#fff', bgColor: '#dc2626' } },

        // Code Picker
        { provide: CODE_PICKER_SERVICE, useClass: CodePickerService },

        // Google Maps
        {
            provide: GOOGLE_MAPS_API_CONFIG,
            useValue: {
                apiVersion: 'beta',
                libraries: ['places', 'geometry', 'marker'],
                apiKey: ConfigService.environment.mappingKey
            }
        },

        // Title Strategy
        { provide: TitleStrategy, useClass: CustomPageTitleStrategy },

        // Query DevTools (dev only)
        ...(ConfigService.environment.environment === 'dev' && !isPreviewOrPublic
            ? [provideQueryDevTools({ buttonPosition: 'bottom-left' })]
            : []),

        // Import remaining module providers (NgRx, 3rd party modules, etc.)
        importProvidersFrom(ApplicationModule),

        provideAnimations(),

        // Markdown rendering (ngx-markdown)
        provideMarkdown(),
        // provideServiceWorker('sw-master.js', {
        //     enabled: true,
        //     registrationStrategy: 'registerWhenStable:30000'
        //   })

    ]
};
